// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import Material from '../renderer/Material';
import FullscreenUtils from '../renderer/pass/FullscreenUtils';
import RenderTarget from '../renderer/pass/RenderTarget';
import ObjectUtils from '../util/ObjectUtils';
import ShaderLib from '../renderer/shaders/ShaderLib';
import ShaderLibExtra from '../passpack/ShaderLibExtra';
import Pass from '../renderer/pass/Pass';

/**
 * <pre>
 * settings: {
 *     strength: 1.0,
 *     sigma: 4.0,
 *     sizeX: 256,
 *     sizeY: 256
 * }
 * </pre>
 */
class BloomPass extends Pass {
  constructor(settings) {
    super();
    settings = settings || {};

    this.target = settings.target !== undefined ? settings.target : null;
    const strength = settings.strength !== undefined ? settings.strength : 0.0;
    const sigma = settings.sigma !== undefined ? settings.sigma : 4.0;
    const kernelSize = 2 * Math.ceil(sigma * 3.0) + 1;
    this.downsampleAmount = settings.downsampleAmount !== undefined ? Math.max(settings.downsampleAmount, 1) : 4;

    const width = window.innerWidth || 1024;
    const height = window.innerHeight || 1024;
    this.updateSize({
      x: 0,
      y: 0,
      width,
      height,
    });

    this.renderable = {
      meshData: FullscreenUtils.quad,
      materials: [],
    };

    this.copyMaterial = new Material(ShaderLib.copyPure);
    this.copyMaterial.uniforms.opacity = strength;
    this.copyMaterial.blendState.blending = 'AdditiveBlending';

    this.convolutionShader = ObjectUtils.deepClone(ShaderLib.convolution);
    this.convolutionShader.defines = {
      KERNEL_SIZE_FLOAT: kernelSize.toFixed(1),
      KERNEL_SIZE_INT: kernelSize.toFixed(0),
    };
    this.convolutionMaterial = new Material(this.convolutionShader);
    this.convolutionMaterial.uniforms.uImageIncrement = BloomPass.blurX;
    this.convolutionMaterial.uniforms.cKernel = this.convolutionShader.buildKernel(sigma);

    this.bcMaterial = new Material(ShaderLibExtra.brightnesscontrast);
    this.bcMaterial.uniforms.brightness = 0.0;
    this.bcMaterial.uniforms.contrast = 0.0;

    this.enabled = true;
    this.clear = false;
    this.needsSwap = false;
  }

  destroy(renderer) {
    if (this.renderTargetX) {
      this.renderTargetX.destroy(renderer.context);
    }
    if (this.renderTargetY) {
      this.renderTargetY.destroy(renderer.context);
    }
    this.convolutionMaterial.shader.destroy();
    this.copyMaterial.shader.destroy();
    this.bcMaterial.shader.destroy();
  }

  invalidateHandles(renderer) {
    renderer.invalidateMaterial(this.convolutionMaterial);
    renderer.invalidateMaterial(this.copyMaterial);
    renderer.invalidateMaterial(this.convolutionMaterial);
    renderer.invalidateMaterial(this.bcMaterial);
    renderer.invalidateRenderTarget(this.renderTargetX);
    renderer.invalidateRenderTarget(this.renderTargetY);
    renderer.invalidateMeshData(this.renderable.meshData);
  }

  updateSize(size, renderer) {
    const sizeX = size.width / this.downsampleAmount;
    const sizeY = size.height / this.downsampleAmount;
    if (this.renderTargetX) {
      this.renderTargetX.destroy(renderer.context);
    }
    if (this.renderTargetY) {
      this.renderTargetY.destroy(renderer.context);
    }
    this.renderTargetX = new RenderTarget(sizeX, sizeY);
    this.renderTargetY = new RenderTarget(sizeX, sizeY);
  }

  render(renderer, writeBuffer, readBuffer) {
    // Brightness & contrast
    this.renderable.materials[0] = this.bcMaterial;

    this.bcMaterial.setTexture('DIFFUSE_MAP', readBuffer);
    renderer.render(this.renderable, null, FullscreenUtils.camera, [], this.renderTargetY, true);

    // Blur Y
    this.renderable.materials[0] = this.convolutionMaterial;

    this.convolutionMaterial.setTexture('DIFFUSE_MAP', this.renderTargetY);
    this.convolutionMaterial.uniforms.uImageIncrement = BloomPass.blurY;

    renderer.render(this.renderable, null, FullscreenUtils.camera, [], this.renderTargetX, true);

    // Blur X
    this.convolutionMaterial.setTexture('DIFFUSE_MAP', this.renderTargetX);
    this.convolutionMaterial.uniforms.uImageIncrement = BloomPass.blurX;

    renderer.render(this.renderable, null, FullscreenUtils.camera, [], this.renderTargetY, true);

    // Additive blend
    this.renderable.materials[0] = this.copyMaterial;
    this.copyMaterial.setTexture('DIFFUSE_MAP', this.renderTargetY);

    if (this.target !== null) {
      renderer.render(this.renderable, null, FullscreenUtils.camera, [], this.target, this.clear);
    } else {
      renderer.render(this.renderable, null, FullscreenUtils.camera, [], readBuffer, this.clear);
    }
  }
}

BloomPass.blurX = [0.001953125, 0.0];
BloomPass.blurY = [0.0, 0.001953125];

export default BloomPass;
